import React, { useState, useEffect } from 'react';
import Navbar from '../components/Navbar';
import { getChallenges, createChallenge, getChallenge, logChallengeProgress, completeChallenge, extractErrorMessage } from '../services/api';
import { FaPlus, FaTrophy, FaCheck, FaChevronDown, FaChevronRight } from 'react-icons/fa';
import UserSelect from '../components/UserSelect';
import { useToast } from '../components/Toast';

function Challenges({ user }) {
  const toast = useToast();
  const [challenges, setChallenges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showProgressModal, setShowProgressModal] = useState(false);
  const [savingCreate, setSavingCreate] = useState(false);
  const [savingProgress, setSavingProgress] = useState(false);
  const [formErrorCreate, setFormErrorCreate] = useState(null);
  const [formErrorProgress, setFormErrorProgress] = useState(null);
  const [expandedChallenge, setExpandedChallenge] = useState(null);
  const [challengeDetail, setChallengeDetail] = useState(null);
  const [newChallenge, setNewChallenge] = useState({
    user_id: '',
    title: '',
    description: '',
    duration_days: 7,
    rules: ''
  });
  const [progressEntry, setProgressEntry] = useState({
    challenge_id: '',
    day: 1,
    completed: true,
    notes: ''
  });

  useEffect(() => {
    fetchChallenges();
  }, []);

  const fetchChallenges = async () => {
    try {
      setLoading(true);
      const data = await getChallenges();
      setChallenges(data.challenges || data || []);
    } catch (error) {
      console.error('Error fetching challenges:', error);
      toast.error(extractErrorMessage(error, 'Challenges konnten nicht geladen werden'));
    } finally {
      setLoading(false);
    }
  };

  const fetchChallengeDetail = async (id) => {
    try {
      const data = await getChallenge(id);
      setChallengeDetail(data);
    } catch (error) {
      console.error('Error fetching challenge detail:', error);
      toast.error(extractErrorMessage(error, 'Challenge-Details konnten nicht geladen werden'));
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (savingCreate) return;
    setSavingCreate(true);
    setFormErrorCreate(null);
    try {
      await createChallenge(newChallenge);
      setNewChallenge({ user_id: '', title: '', description: '', duration_days: 7, rules: '' });
      setShowCreateModal(false);
      toast.success('Challenge erstellt');
      fetchChallenges();
    } catch (error) {
      console.error('Error creating challenge:', error);
      setFormErrorCreate(extractErrorMessage(error, 'Fehler beim Erstellen der Challenge'));
    } finally {
      setSavingCreate(false);
    }
  };

  const handleLogProgress = async (e) => {
    e.preventDefault();
    if (savingProgress) return;
    setSavingProgress(true);
    setFormErrorProgress(null);
    try {
      await logChallengeProgress(progressEntry.challenge_id, progressEntry);
      setProgressEntry({ challenge_id: '', day: 1, completed: true, notes: '' });
      setShowProgressModal(false);
      toast.success('Fortschritt geloggt');
      if (expandedChallenge) fetchChallengeDetail(expandedChallenge);
      fetchChallenges();
    } catch (error) {
      console.error('Error logging progress:', error);
      setFormErrorProgress(extractErrorMessage(error, 'Fehler beim Loggen des Fortschritts'));
    } finally {
      setSavingProgress(false);
    }
  };

  const handleComplete = async (id) => {
    if (!window.confirm('Challenge als abgeschlossen markieren?')) return;
    try {
      await completeChallenge(id);
      toast.success('Challenge abgeschlossen');
      fetchChallenges();
    } catch (error) {
      console.error('Error completing challenge:', error);
      toast.error(extractErrorMessage(error, 'Fehler beim Abschließen der Challenge'));
    }
  };

  const closeCreateModal = () => {
    if (savingCreate) return;
    setShowCreateModal(false);
    setFormErrorCreate(null);
  };

  const closeProgressModal = () => {
    if (savingProgress) return;
    setShowProgressModal(false);
    setFormErrorProgress(null);
  };

  const toggleChallenge = async (id) => {
    if (expandedChallenge === id) {
      setExpandedChallenge(null);
      setChallengeDetail(null);
    } else {
      setExpandedChallenge(id);
      await fetchChallengeDetail(id);
    }
  };

  const getProgressPercent = (challenge) => {
    if (!challenge.duration_days) return 0;
    const completed = challenge.days_completed || 0;
    return Math.min(100, Math.round((completed / challenge.duration_days) * 100));
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      active: { label: 'Aktiv', className: 'badge badge-success' },
      completed: { label: 'Abgeschlossen', className: 'badge badge-info' },
      failed: { label: 'Fehlgeschlagen', className: 'badge badge-danger' },
      paused: { label: 'Pausiert', className: 'badge badge-warning' }
    };
    const s = statusMap[status] || { label: status, className: 'badge' };
    return <span className={s.className}>{s.label}</span>;
  };

  return (
    <div className="app-layout">
      <Navbar user={user} />
      <main className="main-content">
        <div className="page-header">
          <h1>Challenges</h1>
          <button className="add-btn" onClick={() => setShowCreateModal(true)}>
            <FaPlus /> Neue Challenge
          </button>
        </div>

        {loading ? (
          <div className="loading-container">
            <div className="spinner"></div>
          </div>
        ) : (
          <div className="data-table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th></th>
                  <th>Titel</th>
                  <th>Status</th>
                  <th>Dauer</th>
                  <th>Fortschritt</th>
                  <th>Erstellt</th>
                  <th>Aktionen</th>
                </tr>
              </thead>
              <tbody>
                {challenges.length > 0 ? challenges.map(ch => (
                  <React.Fragment key={ch.id}>
                    <tr className={expandedChallenge === ch.id ? 'expanded-row' : ''}>
                      <td>
                        <button className="icon-btn" onClick={() => toggleChallenge(ch.id)}>
                          {expandedChallenge === ch.id ? <FaChevronDown /> : <FaChevronRight />}
                        </button>
                      </td>
                      <td><strong>{ch.title}</strong></td>
                      <td>{getStatusBadge(ch.status)}</td>
                      <td>{ch.duration_days} Tage</td>
                      <td>
                        <div className="progress-bar">
                          <div className="progress-fill" style={{ width: `${getProgressPercent(ch)}%` }}></div>
                        </div>
                        <span className="progress-text">{getProgressPercent(ch)}%</span>
                      </td>
                      <td>{new Date(ch.created_at || ch.timestamp).toLocaleDateString('de-DE')}</td>
                      <td className="actions-cell">
                        {ch.status === 'active' && (
                          <>
                            <button className="icon-btn edit" onClick={() => { setProgressEntry({ ...progressEntry, challenge_id: ch.id }); setShowProgressModal(true); }} title="Fortschritt loggen">
                              <FaPlus />
                            </button>
                            <button className="icon-btn success" onClick={() => handleComplete(ch.id)} title="Abschließen">
                              <FaTrophy />
                            </button>
                          </>
                        )}
                      </td>
                    </tr>
                    {expandedChallenge === ch.id && challengeDetail && (
                      <tr className="sub-row">
                        <td colSpan="7">
                          <div className="sub-table-container">
                            <div className="detail-section">
                              {ch.description && <p><strong>Beschreibung:</strong> {ch.description}</p>}
                              {ch.rules && <p><strong>Regeln:</strong> {ch.rules}</p>}
                            </div>
                            <h4>Tagesfortschritt</h4>
                            {(challengeDetail.progress || []).length > 0 ? (
                              <table className="data-table sub-table">
                                <thead>
                                  <tr>
                                    <th>Tag</th>
                                    <th>Status</th>
                                    <th>Notizen</th>
                                    <th>Datum</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {(challengeDetail.progress || []).map((p, idx) => (
                                    <tr key={idx}>
                                      <td>Tag {p.day}</td>
                                      <td>{p.completed ? <span className="badge badge-success"><FaCheck /> Geschafft</span> : <span className="badge badge-danger">Nicht geschafft</span>}</td>
                                      <td>{p.notes || '-'}</td>
                                      <td>{p.created_at ? new Date(p.created_at).toLocaleDateString('de-DE') : '-'}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            ) : (
                              <p className="no-data">Noch kein Fortschritt geloggt</p>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                )) : (
                  <tr><td colSpan="7" className="no-data">Keine Challenges gefunden</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {showCreateModal && (
          <div className="modal-overlay" onClick={closeCreateModal}>
            <div className="modal" onClick={e => e.stopPropagation()} role="dialog" aria-modal="true">
              <h2>Neue Challenge</h2>
              {formErrorCreate && <div className="shared-form-error-banner">{formErrorCreate}</div>}
              <form onSubmit={handleCreate}>
                <div className="form-group">
                  <UserSelect
                    value={newChallenge.user_id}
                    onChange={id => setNewChallenge({...newChallenge, user_id: id})}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Titel *</label>
                  <input
                    type="text"
                    value={newChallenge.title}
                    onChange={e => setNewChallenge({...newChallenge, title: e.target.value})}
                    placeholder="Challenge-Titel"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Beschreibung</label>
                  <textarea
                    value={newChallenge.description}
                    onChange={e => setNewChallenge({...newChallenge, description: e.target.value})}
                    placeholder="Worum geht es bei dieser Challenge?"
                  />
                </div>
                <div className="form-group">
                  <label>Dauer (Tage)</label>
                  <input
                    type="number"
                    min="1"
                    max="365"
                    value={newChallenge.duration_days}
                    onChange={e => setNewChallenge({...newChallenge, duration_days: parseInt(e.target.value) || 7})}
                  />
                </div>
                <div className="form-group">
                  <label>Regeln</label>
                  <textarea
                    value={newChallenge.rules}
                    onChange={e => setNewChallenge({...newChallenge, rules: e.target.value})}
                    placeholder="Regeln für die Challenge..."
                  />
                </div>
                <div className="modal-actions">
                  <button type="button" className="cancel-btn" onClick={closeCreateModal} disabled={savingCreate}>Abbrechen</button>
                  <button type="submit" className="submit-btn" disabled={savingCreate}>
                    {savingCreate ? 'Speichert\u2026' : 'Erstellen'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {showProgressModal && (
          <div className="modal-overlay" onClick={closeProgressModal}>
            <div className="modal" onClick={e => e.stopPropagation()} role="dialog" aria-modal="true">
              <h2>Fortschritt loggen</h2>
              {formErrorProgress && <div className="shared-form-error-banner">{formErrorProgress}</div>}
              <form onSubmit={handleLogProgress}>
                <div className="form-group">
                  <label>Tag</label>
                  <input
                    type="number"
                    min="1"
                    value={progressEntry.day}
                    onChange={e => setProgressEntry({...progressEntry, day: parseInt(e.target.value) || 1})}
                  />
                </div>
                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={progressEntry.completed}
                      onChange={e => setProgressEntry({...progressEntry, completed: e.target.checked})}
                    />
                    Heute geschafft
                  </label>
                </div>
                <div className="form-group">
                  <label>Notizen</label>
                  <textarea
                    value={progressEntry.notes}
                    onChange={e => setProgressEntry({...progressEntry, notes: e.target.value})}
                    placeholder="Wie lief der Tag?"
                  />
                </div>
                <div className="modal-actions">
                  <button type="button" className="cancel-btn" onClick={closeProgressModal} disabled={savingProgress}>Abbrechen</button>
                  <button type="submit" className="submit-btn" disabled={savingProgress}>
                    {savingProgress ? 'Speichert\u2026' : 'Loggen'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

export default Challenges;
